import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useCart } from "../../context/CartContext";
import { Link } from "react-router-dom";
import {
  ShoppingBagIcon,
  HeartIcon,
  HeartSolidIcon,
  StarSolidIcon,
} from "../../utils/icons";
import { formatCurrency } from "../../utils/formatters";
import Button from "./Button";

export default function ProductCard({ product }) {
  const { addToCart } = useCart();
  const [isWishlist, setIsWishlist] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product);
  };

  const handleWishlist = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsWishlist(!isWishlist);
  };

  return (
    <Link to={`/products/${product.id}`}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="group relative"
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
      >
        <div className="relative aspect-[3/4] overflow-hidden rounded-xl bg-gray-100">
          <motion.img
            src={product.image}
            alt={product.name}
            className="absolute inset-0 h-full w-full object-cover object-center"
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.6 }}
          />

          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

          {/* Quick Actions */}
          <AnimatePresence>
            {isHovered && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                className="absolute bottom-4 left-4 right-4 flex justify-end"
              >
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={handleWishlist}
                  className="p-2 rounded-full bg-white/90 hover:bg-white text-primary-900 shadow-md backdrop-blur-sm"
                >
                  {isWishlist ? (
                    <HeartSolidIcon className="h-5 w-5 text-[#DB5461]" />
                  ) : (
                    <HeartIcon className="h-5 w-5" />
                  )}
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Best Seller Badge */}
          {product.featured && (
            <div className="absolute top-4 left-4">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="px-3 py-1.5 rounded-full bg-[#DB5461] text-white text-xs font-medium shadow-md"
              >
                Best Seller
              </motion.div>
            </div>
          )}
        </div>

        <div className="mt-4 space-y-2">
          <div className="flex justify-between items-start">
            <div className="group/title">
              <h3 className="text-lg font-medium text-gray-900 group-hover/title:text-[#DB5461] transition-colors duration-300">
                {product.name}
              </h3>
            </div>
            <div className="flex items-center">
              <StarSolidIcon className="h-5 w-5 text-[#DB5461]" />
              <span className="ml-1 text-sm text-gray-600">4.5</span>
            </div>
          </div>

          <p className="text-sm text-gray-500">{product.category}</p>

          <div className="flex justify-between items-center pt-2">
            <p className="text-lg font-serif font-medium text-primary-900">
              {formatCurrency(product.price)}
            </p>
            <Button
              variant="primary"
              size="sm"
              onClick={handleAddToCart}
              className="!bg-[#DB5461] hover:!bg-[#DB5461]/90 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            >
              <ShoppingBagIcon className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </motion.div>
    </Link>
  );
}
